import { ReactElement } from 'react';
import { IconNames } from 'pages/NewOverview/types';
import * as icons from 'icons';
import { UnitCardContainer, UnitCardIconContainer } from './styles';
import { colors } from '~/styles/colors';
import { Button } from '~/components';
import { HealthVariant } from './types';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

interface UnitCardProps {
  value?: number;
  variant: HealthVariant;
  onClickDetails?: () => void;
}

interface UnitCardIconProps {
  color: string;
  icon: IconNames;
  disabled: boolean;
  variant: HealthVariant;
}

interface VariantConfig {
  cardColor: string;
  iconColor: string;
  icon: IconNames;
  description: string;
}

const variantConfigs: Record<HealthVariant, VariantConfig> = {
  alert: {
    cardColor: colors.bg['warn-light'],
    iconColor: colors.state.warning.base,
    description: t('unidadesEmAlerta'),
    icon: 'WarnCircle',
  },
  critical: {
    cardColor: colors.bg['danger-light-0'],
    iconColor: colors.state.danger.base,
    description: t('unidadesEmAlertaCritico'),
    icon: 'XCircle1Icon',
  },
  healthy: {
    cardColor: colors.bg['success-light'],
    iconColor: colors.state.success.base,
    description: t('unidadesSaudaveis'),
    icon: 'CheckVerified1Icon',
  },
  inoperative: {
    cardColor: colors.bg['weak-100'],
    iconColor: colors.bg['soft-400'],
    description: t('unidadesOffline'),
    icon: 'SlashCircle1Icon',
  },
};

export function UnitCard({
  variant,
  value,
  onClickDetails,
}: Readonly<UnitCardProps>): ReactElement {
  const { t } = useTranslation();
  const config = variantConfigs[variant];
  const disabled = value === undefined;

  return (
    <UnitCardContainer
      backgroundColor={config.cardColor}
      disabledCard={disabled}
    >
      <UnitCardIcon
        disabled={disabled}
        icon={config.icon}
        color={config.iconColor}
        variant={variant}
      />
      <h5>{value ?? '-'}</h5>
      <h6>{config.description}</h6>
      {!disabled && onClickDetails !== undefined && (
        <Button
          onClick={onClickDetails}
          type="button"
          variant="primary-ghost"
          style={{ width: 105 }}
        >
          {t('verDetalhes')}
        </Button>
      )}
    </UnitCardContainer>
  );
}

function UnitCardIcon({
  disabled,
  icon,
  color,
  variant,
}: Readonly<UnitCardIconProps>): ReactElement {
  const Icon = icons[icon];
  const size = variant === 'alert' || variant === 'inoperative' ? 16 : 22;

  return (
    <UnitCardIconContainer
      backgroundColor={color}
      disabled={disabled}
    >
      <Icon color={colors.icon['white-0']} size={size} />
    </UnitCardIconContainer>
  );
}
